import { useSyncExternalStore } from 'react';
import type { IndexInstrument, Quote, QuotePatch } from '../protocol/types';

type Listener = () => void;

export interface QuoteStatusSnapshot {
  connected: boolean;
  message: string;
}

export interface QuoteStatsSnapshot {
  totalUpdates: number;
  lastSeq: number;
  lastUpdateTime: number;
  dropped: number;
  quoteCount: number;
}

const EMPTY_UNIVERSE: string[] = Object.freeze([]) as unknown as string[];
const EMPTY_INDEXES: IndexInstrument[] = Object.freeze([]) as unknown as IndexInstrument[];

class QuoteStore {
  private quotes = new Map<string, Quote>();
  private emptyQuotes = new Map<string, Quote>();

  private symbolListeners = new Map<string, Set<Listener>>();
  private universeListeners = new Set<Listener>();
  private indexListeners = new Set<Listener>();
  private statusListeners = new Set<Listener>();
  private statsListeners = new Set<Listener>();

  private universe: string[] = EMPTY_UNIVERSE;
  private indexes: IndexInstrument[] = EMPTY_INDEXES;

  private connected = false;
  private statusMessage = 'Not connected';

  private totalUpdates = 0;
  private lastSeq = 0;
  private lastUpdateTime = 0;
  private dropped = 0;

  private statusSnapshot: QuoteStatusSnapshot = Object.freeze({
    connected: false,
    message: 'Not connected'
  });

  private statsSnapshot: QuoteStatsSnapshot = Object.freeze({
    totalUpdates: 0,
    lastSeq: 0,
    lastUpdateTime: 0,
    dropped: 0,
    quoteCount: 0
  });

  setUniverse(symbols: string[]): void {
    const next = Object.freeze(
      [...new Set(symbols.map(normalizeSymbol).filter(Boolean))]
    ) as string[];

    if (arraysEqual(this.universe, next)) return;

    this.universe = next;
    this.emit(this.universeListeners);
  }

  getUniverse = (): string[] => this.universe;

  subscribeUniverse = (listener: Listener): (() => void) => {
    this.universeListeners.add(listener);
    return () => this.universeListeners.delete(listener);
  };

  setIndexes(indexes: IndexInstrument[]): void {
    const next = Object.freeze(
      indexes.map((item) =>
        Object.freeze({
          ...item,
          symbol: normalizeSymbol(item.symbol)
        })
      )
    ) as IndexInstrument[];

    if (indexesEqual(this.indexes, next)) return;

    this.indexes = next;
    this.emit(this.indexListeners);
  }

  getIndexes = (): IndexInstrument[] => this.indexes;

  subscribeIndexes = (listener: Listener): (() => void) => {
    this.indexListeners.add(listener);
    return () => this.indexListeners.delete(listener);
  };

  setStatus(connected: boolean, message: string): void {
    if (this.connected === connected && this.statusMessage === message) return;

    this.connected = connected;
    this.statusMessage = message;

    this.statusSnapshot = Object.freeze({
      connected,
      message
    });

    this.emit(this.statusListeners);
  }

  getStatus = (): QuoteStatusSnapshot => this.statusSnapshot;

  subscribeStatus = (listener: Listener): (() => void) => {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  };

  applySnapshot(quotes: QuotePatch[], seq: number): void {
    this.applyPatch(quotes, seq);
  }

  applyPatch(updates: QuotePatch[], seq: number, dropped?: number): void {
    if (updates.length === 0 && !dropped) return;

    this.lastSeq = seq;
    this.totalUpdates += updates.length;
    this.lastUpdateTime = Date.now();
    this.dropped += dropped ?? 0;

    const changed = new Set<string>();

    for (const update of updates) {
      const symbol = normalizeSymbol(update.symbol);
      const previous = this.quotes.get(symbol) ?? this.getEmptyQuote(symbol);

      const next = freezeQuote({
        ...previous,
        ...update,
        symbol
      });

      if (quotesEqual(previous, next)) continue;

      this.quotes.set(symbol, next);
      this.emptyQuotes.delete(symbol);
      changed.add(symbol);
    }

    changed.forEach((symbol) => {
      const listeners = this.symbolListeners.get(symbol);
      if (listeners) this.emit(listeners);
    });

    this.statsSnapshot = Object.freeze({
      totalUpdates: this.totalUpdates,
      lastSeq: this.lastSeq,
      lastUpdateTime: this.lastUpdateTime,
      dropped: this.dropped,
      quoteCount: this.quotes.size
    });

    this.emit(this.statsListeners);
  }

  getQuote = (symbol: string): Quote => {
    const normalizedSymbol = normalizeSymbol(symbol);
    return this.quotes.get(normalizedSymbol) ?? this.getEmptyQuote(normalizedSymbol);
  };

  subscribeQuote = (symbol: string, listener: Listener): (() => void) => {
    const normalizedSymbol = normalizeSymbol(symbol);

    let listeners = this.symbolListeners.get(normalizedSymbol);

    if (!listeners) {
      listeners = new Set();
      this.symbolListeners.set(normalizedSymbol, listeners);
    }

    listeners.add(listener);

    return () => {
      listeners?.delete(listener);

      if (listeners && listeners.size === 0) {
        this.symbolListeners.delete(normalizedSymbol);
      }
    };
  };

  getStats = (): QuoteStatsSnapshot => this.statsSnapshot;

  subscribeStats = (listener: Listener): (() => void) => {
    this.statsListeners.add(listener);
    return () => this.statsListeners.delete(listener);
  };

  private getEmptyQuote(symbol: string): Quote {
    const normalizedSymbol = normalizeSymbol(symbol);
    const existing = this.emptyQuotes.get(normalizedSymbol);

    if (existing) return existing;

    const quote = freezeQuote(emptyQuote(normalizedSymbol));
    this.emptyQuotes.set(normalizedSymbol, quote);

    return quote;
  }

  private emit(listeners: Set<Listener>): void {
    Array.from(listeners).forEach((listener) => listener());
  }
}

function emptyQuote(symbol: string): Quote {
  return {
    symbol,
    last: 0,
    bid: 0,
    ask: 0,
    bidSize: 0,
    askSize: 0,
    change: 0,
    changePct: 0,
    volume: 0,
    high: 0,
    low: 0,
    open: 0,
    exchange: '—',
    time: 0,
    direction: 0
  };
}

function freezeQuote(quote: Quote): Quote {
  return Object.freeze(quote);
}

function normalizeSymbol(symbol: string): string {
  return symbol.trim().toUpperCase();
}

function arraysEqual(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }

  return true;
}

function indexesEqual(left: readonly IndexInstrument[], right: readonly IndexInstrument[]): boolean {
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (
      left[index].symbol !== right[index].symbol ||
      left[index].name !== right[index].name
    ) {
      return false;
    }
  }

  return true;
}

function quotesEqual(left: Quote, right: Quote): boolean {
  return (
    left.symbol === right.symbol &&
    left.last === right.last &&
    left.bid === right.bid &&
    left.ask === right.ask &&
    left.bidSize === right.bidSize &&
    left.askSize === right.askSize &&
    left.change === right.change &&
    left.changePct === right.changePct &&
    left.volume === right.volume &&
    left.high === right.high &&
    left.low === right.low &&
    left.open === right.open &&
    left.exchange === right.exchange &&
    left.time === right.time &&
    left.direction === right.direction
  );
}

export const quoteStore = new QuoteStore();

export function useQuote(symbol: string): Quote {
  const normalizedSymbol = normalizeSymbol(symbol);

  return useSyncExternalStore(
    (listener) => quoteStore.subscribeQuote(normalizedSymbol, listener),
    () => quoteStore.getQuote(normalizedSymbol),
    () => quoteStore.getQuote(normalizedSymbol)
  );
}

export function useQuoteUniverse(): string[] {
  return useSyncExternalStore(
    quoteStore.subscribeUniverse,
    quoteStore.getUniverse,
    quoteStore.getUniverse
  );
}

export function useIndexUniverse(): IndexInstrument[] {
  return useSyncExternalStore(
    quoteStore.subscribeIndexes,
    quoteStore.getIndexes,
    quoteStore.getIndexes
  );
}

export function useQuoteStatus(): QuoteStatusSnapshot {
  return useSyncExternalStore(
    quoteStore.subscribeStatus,
    quoteStore.getStatus,
    quoteStore.getStatus
  );
}

export function useQuoteStats(): QuoteStatsSnapshot {
  return useSyncExternalStore(
    quoteStore.subscribeStats,
    quoteStore.getStats,
    quoteStore.getStats
  );
}